import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MoreVertical, Trash2, Lock, Globe } from 'lucide-react';
import { useMoodboard } from '@/hooks/use-moodboards';
import { useMoodboardItems, useRemoveFromMoodboard } from '@/hooks/use-moodboard-items';
import BulletinLightbox from '@/components/bulletins/BulletinLightbox';
import { Button } from '@/components/ui/button';
import Portal from '@/components/ui/portal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function MoodboardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: moodboard } = useMoodboard(id);
  const { data: items = [] } = useMoodboardItems(id);
  const removeItem = useRemoveFromMoodboard();

  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<any[]>([]);

  const handleImageClick = (item: any) => {
    // Create bulletin-like objects for the lightbox
    const bulletinItems = items
      .filter(i => i.content_type === 'bulletin')
      .map(i => ({
        id: i.content_id,
        image_url: i.image_url,
        content: i.caption,
        user_id: i.author_id,
        created_at: i.original_created_at,
        profiles: {
          username: i.author_username,
        },
      }));

    setLightboxImages(bulletinItems);
    setSelectedImageId(item.content_id);
  };

  const handleDelete = (itemId: string) => {
    if (confirm('Remove this item from your moodboard?')) {
      removeItem.mutate({ itemId, moodboardId: id! });
    }
  };

  if (!moodboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const featuredItem = items[0];

  return (
    <>
      {/* Floating Back Button - Rendered in Portal to escape stacking context */}
      <Portal>
        <div className="fixed top-20 left-6 z-[100] md:top-[88px] md:left-[280px]">
          <button
            onClick={() => navigate('/moodboards')}
            className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
      </Portal>

      <div className="min-h-screen bg-[#1A1A1B]">

        {/* Full-Screen Featured Image */}
        {featuredItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative h-screen w-full cursor-pointer"
            onClick={() => handleImageClick(featuredItem)}
          >
            <img
              src={featuredItem.image_url}
              alt="Featured"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/40 pointer-events-none" />
            
            {/* Moodboard Info - Bottom Left */}
            <div className="absolute bottom-24 left-6 right-6">
              <div className="flex items-center gap-2 mb-1">
                {moodboard.is_private && <Lock className="w-3 h-3 text-white/70" />}
              </div>
              <h1 className="text-4xl font-bold text-white mb-1 drop-shadow-2xl">
                {moodboard.name}
              </h1>
              <p className="text-sm text-white/80 flex items-center gap-1.5 drop-shadow-lg">
                <span className="text-lg">{moodboard.icon}</span>
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </motion.div>
        )}

        {/* Mosaic Grid - iOS Photos Style */}
        {items.length > 0 && (
          <div className="bg-[#1A1A1B] px-2 pt-2 pb-24">
            <div className="grid grid-cols-3 gap-1">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                  className="relative aspect-square cursor-pointer overflow-hidden rounded-lg"
                  onClick={() => handleImageClick(item)}
                >
                  <img
                    src={item.image_url}
                    alt=""
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {items.length === 0 && (
          <div className="text-center py-20 px-6">
            <div className="text-6xl mb-4">{moodboard.icon}</div>
            <h2 className="text-2xl font-bold mb-2 text-white">{moodboard.name}</h2>
            <p className="text-white/60 mb-6">
              This moodboard is empty. Start saving posts to build your collection!
            </p>
            <Button onClick={() => navigate('/bulletins')}>
              Explore Content
            </Button>
          </div>
        )}
      </div>

      {/* Lightbox - Rendered in Portal to escape stacking context */}
      {selectedImageId && lightboxImages.length > 0 && (
        <Portal>
          <BulletinLightbox
            bulletins={lightboxImages}
            selectedId={selectedImageId}
            onNavigate={setSelectedImageId}
            onClose={() => setSelectedImageId(null)}
          />
        </Portal>
      )}
    </>
  );
}
